// @flow

import React, { type ComponentType } from 'react';
import styled from 'styled-components';
import { colors } from 'seedling/constants';
import Tooltip from 'seedling/components/tooltip';

type Icon = ComponentType<{ size: string }>;

type Props = {
  onClick: (SyntheticEvent<HTMLButtonElement>) => void,
  size: 'small' | 'medium',
  Icon: Icon,
  disabled?: boolean,
  // Both the content of the tooltip and the "accessible name" of the button
  // https://www.w3.org/TR/accname-1.1/
  label: string,
  className?: string,
  'data-provides'?: string,
};

const ICON_SIZES = {
  small: '16px',
  medium: '20px',
};

const BUTTON_SIZES = {
  small: '24px',
  medium: '32px',
};

const StyledButton = styled.button`
  background-color: transparent;
  border-color: transparent;
  padding: 0;
  border-radius: 100%;
  height: ${props => props.size};
  width: ${props => props.size};

  svg {
    vertical-align: middle;
  }

  ${props =>
    !props.disabled &&
    `
    cursor: pointer;

    &:active {
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.15);
    }

    &:hover,
    &:focus {
      background-color: ${colors.toddlerBlue};
      svg {
        path {
          fill: ${colors.darkGrey};
        }
      }
    }
  `}

  ${props =>
    props.disabled &&
    `
    cursor: default;

    svg {
      path {
        fill: ${colors.mediumLightGrey};
      }
    }

    &:hover,
    &:focus {
      svg {
        cursor: default;

        path {
          fill: ${colors.mediumLightGrey};
        }
      }
    }
  `}
`;

const IconButton = (props: Props) => {
  const { Icon, onClick, size, disabled = false, label, className } = props;
  return (
    <Tooltip placement="top" content={label} className={className}>
      <StyledButton
        size={BUTTON_SIZES[size]}
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        data-provides={props['data-provides']}
      >
        <Icon size={ICON_SIZES[size]} marginLeft="0" marginRight="0" />
      </StyledButton>
    </Tooltip>
  );
};

export default IconButton;
